<template>
	<div class="w-100 h-100">
		<b-container fluid>
			<h2 class="tw-mb-0 text-aegis-dk">Luminex</h2>
			<p class="tw-italic tw-text-gray-700">Batch Upload with CSV file and Data View</p>
		</b-container>

		<hr />

		<b-card no-body>
			<b-tabs card fill>
				<b-tab
					:title="'Batch Uploads'"
					active
				>
					<b-card-text>
						<b-container>
							<b-row>
								<b-col>
									<b-button
										variant="secondary"
										size="sm"
										class="ml-1"
										@click="downloadTemplate"
									>
										Download Template
									</b-button>
								</b-col>
							</b-row>
							<!-- file upload form -->
							<b-row>
								<b-col md="6" offset-md="3" class="mt-3">
									<b-form-file
										id="file"
										v-model="selectedFile"
										:state="Boolean(selectedFile)"
										placeholder="Choose a file or drop it here..."
										drop-placeholder="Drop file here..."
										size="lg"
										accept="text/csv, .csv"
									></b-form-file>
								</b-col>
							</b-row>

							<!-- alert if uploaded file is incorrect -->
							<b-row>
								<b-col md="6" offset-md="3" class="mt-3">
									<b-alert
										v-if="uploading_error"
										show
										dismissible
										variant="danger"
									>
										Found error(s). Please check the data file and reupload.
									</b-alert>
								</b-col>
							</b-row>

							<!-- Uplod button -->
							<b-row>
								<b-col md="6" offset-md="3" class="mt-3">
									<b-button
										@click="onUpload"
										variant="success"
										size="lg"
										block
										:disabled="uploading"
									>
										{{ uploading ? 'Uploading...' : 'Upload' }}
										<b-spinner
											v-if="uploading"
											small
										></b-spinner>
									</b-button>
								</b-col>
							</b-row>
						</b-container>

						<hr>
						<b-row>
							<b-col>
								<small>
									Total Results: {{ totalRows }} 
								</small>
							</b-col>
						</b-row>

						<b-table
							ref="fileTable"
							striped
							hover
							selectable
							select-mode='single'
							@row-selected="onRowSelected"
							borderless
							:fields="fields"
							:items="myProvider"
							:current-page="currentPage"
							:per-page="perPage"
							:sort-by.sync="sortBy"
							:sort-desc.sync="sortDesc"
							class="table-sm tw-text-sm"
						>
						</b-table>
					</b-card-text>
				</b-tab>
			</b-tabs>
		</b-card>

		<!-- modal view for accepted and rejected records -->
		<b-modal
			id="file-records-modal"
			size="lg"
			scrollable
			:title="set_modal_title"
		>
			<b-row>
				<b-col>
					<small>
						Total Results: {{ modalTotalRows }} 
						<i 
							@click="downloadFileRecordsCsv"
							class="fa fa-download text-success"
							v-b-modal.report-modal
							style="margin-left: 10px;
							cursor: pointer;"
							title="Download File Records"
						></i>
					</small>
				</b-col>
			</b-row>
            <b-table
                responsive
                :items="cptItems"
				:fields="modal_fields"
				tbody-tr-class="luminex-table-cell"
				thead-class="luminex-thead-class"
            />
			<template #modal-footer>
				<b-button
					class="float-left mr-3"
					variant="danger"
					@click="onDelete"
				>Delete File</b-button>
				<b-button class="float-right" @click="hideModal('file-records-modal')">Close</b-button>
			</template>
        </b-modal>
		<!-- modal view for delete file -->
		<b-modal
			id="delete-file-modal"
			v-model="deleteFileModalShow"
			centered
			scrollable
			no-close-on-backdrop
			title="Confirm Upload File Deletion"
			ok-title="Yes, Delete"
			@show="resetModal"
			@hidden="resetModal"
			@ok="handleOk"
			:header-bg-variant="'danger'"
			:header-text-variant="'light'">
			<span v-html="'Are you sure you want to delete this upload file and all associated Luminex records?'"></span>
			<form ref="form" @submit.stop.prevent="handleSubmit">
				<b-form-group
					invalid-feedback="Confirmation is required"
					:state="deleteConfirmState"
				>
				<br>
				<b-form-checkbox
					id="delete-confirm-input"
					name="delete-confirm-chkbx"
					v-model="deleteConfirmed"
				>I'm sure.</b-form-checkbox>
				</b-form-group>
			</form>
		</b-modal>
	</div>
</template>

<script>
import { HTTP } from "@/utilities/http-common";
import HandleErrors from "@/utilities/handle-errors";

import DashboardBaseTemplate from "@/layouts/DashboardBaseTemplate.vue";
import { mapGetters } from "vuex";

export default {
	mixins: [DashboardBaseTemplate],
	data() {
		return {
			baseUrl: `/srilanka/view-luminex-files`,
			selectedFile: null,
			title: "",
			isPrivate: "",
			uploads: [],
			// for loading spinner
			uploading: false,
			// for modal table
			modal_items: [],
			modalTotalRows: 0,
			modal_fields: [],
			// check accepted or rejected for modal table's title
			accepted: true,
			// check file error
			uploading_error: false,
			// check table is being updated
			updating_table: false,
			// for view records: made separate one
			perPage: 15,
			totalRows: 0,
			currentPage: 1,
			sortBy: "id",
			sortDesc: false,
			deleteConfirmed: '',
			deleteConfirmState: null,
			deleteFileModalShow: false,

			// add view items & fields for table view
			fields: [
				{ key: 'id', label: "ID" },
				{ key: 'title', label: "Title" },
				{ key: 'uploaded_time', label: "Uploaded" },
				{ key: 'accepted', label: "Accepted" },
				{ key: 'rejected', label: "Rejected" },
				{ key: 'uploaded_by', label: "Uploaded By" },
			],
			// handling csv loadinf for download records
			csvDownloading: false,
		};
	},
	computed: {
		...mapGetters("user", ['inMyGroups']),
		set_modal_title() {
			return this.file_title + " record(s)";
		},
		cptItems(){
			// add formatting to the row items based on the value in the RowUploadStatus column
			return this.modal_items.map((item)=>{
				let tmp=item;
				if (item.accepted == true){
					tmp._rowVariant='success';
				} else {
					tmp._rowVariant='danger';
				}
				return tmp;
			})  
		}
	},
	created() {},
	mounted() {},
	methods: {
		downloadTemplate(){
			var params = new URLSearchParams();
			var request = {
				params: params,
				responseType: "arraybuffer",
			};
	
			HTTP.get(`/srilanka/download-luminex-template`, request)
			.then((response) => {
				var fileURL = window.URL.createObjectURL(
					new Blob([response.data])
				);
				var fileLink = document.createElement("a");
				fileLink.href = fileURL;
				var fileName = "LuminexUploadTemplate.csv";
				fileLink.setAttribute("download", fileName);
				document.body.appendChild(fileLink);
				fileLink.click();
			})
			.catch((error) => {
				this.err_msg = HandleErrors.HandleErrors(error);
				this.apiErrModalShow = true;
			});
		},
		downloadFileRecordsCsv: function(){
			let that = this;
			var params = new URLSearchParams();
			params.append("return_as", "csv");
			params.append("file_id", that.file_id);

			HTTP.get("/srilanka/view-luminex-file-records", {params: params}).then(response => {
				var fileURL = window.URL.createObjectURL(
					new Blob([response.data])
				);
				var fileLink = document.createElement("a");
				fileLink.href = fileURL;
				fileLink.setAttribute("download", that.file_title + ".csv");
				document.body.appendChild(fileLink);
				fileLink.click();
			});
		},
		onDelete() {
			let that = this;
			that.hideModal('file-records-modal');
			that.deleteFileModalShow = true
		},
		deleteVhtFile: function(){
			let that = this;
			// that.hideModal('file-records-modal');
			const fd = new FormData();
			fd.append("file_id", that.file_id);

			that.uploading_error = false;

			return HTTP.post("/srilanka/archive-luminex-file", fd).then(() => {
				that.$notify({
					text: "File successfully deleted!",
					duration: 10000,
					type: "success",
			});
				that.refresh_files_table();
                that.selectedFile = null;
                that.title = "";
				
			}).catch((error) => {
                // raise alert if uploading error occurs
                that.uploading_error = true;
				that.error_msg = error.response.data.message;
			});
		},
		// for hiding modal
		beforeRouteLeave(to, from, next) {
			this.$bvModal.hide('modal-1');
			next();
		},
		onUpload: function () {
			const that = this;

			if (!that.selectedFile) {
				return that.$bvModal.msgBoxOk("Please select a file!", {
					title: "File Error",
					okVariant: "danger",
					centered: true,
				});
			}

			// initilaize uploading spinner
			that.uploading = true;
			// set initialize uploading_error to be false before upload
			that.uploading_error = false;

			const fd = new FormData();
			fd.append("file", that.selectedFile);
			fd.append("title", that.selectedFile.name);
			fd.append("category", that.category);

			HTTP.post(`/srilanka/upload-luminex-records`, fd)
				.then(() => {
					that.selectedFile = null;
					that.$notify({
						text: "File successfully uploaded!",
						duration: 10000,
						type: "success",
					});

					that.selectedFile = null;
					that.title = "";
					that.isPrivate = false;
					that.sae_case = null;
					that.sae_selected = false;
					that.uploading_error = false;
				})
				.catch((error) => {
					HandleErrors.HandleErrors(error);
					// raise alert if uploading error occurs
					that.uploading_error = true;
				})
				.then(() => {
					that.uploading = false;
					that.refresh_files_table();
				});
		},
		// table view
		myProvider: function(ctx){
			const that = this;
			let params = {
				current_page: ctx.currentPage,
				per_page: ctx.perPage,
				sort_by: ctx.sortBy,
				descending: ctx.sortDesc,
			}

			// check table is being updated
			that.updating_table = true;

			return HTTP.get("/srilanka/view-luminex-files", {
				params: params
			}).then(response => {
				// check table is being updated
				that.updating_table = false;

				that.totalRows = response.data.total_rows
				// since myProvider is called at b-table/items, response data should be returned
				return response.data.data
			}).catch((error) => {
				HandleErrors.HandleErrors(error);
					// check table is being updated
					that.updating_table = false;
			});
    	},
		onRowSelected(items) {
			if(this.inMyGroups(['Data Manager', 'Epi Lead', 'Epi Lab'])) {
				const that = this;
				var params = new URLSearchParams();
				that.file_title = items[0]['title'];
				that.file_id = items[0]['id'];
				params.append("file_id", that.file_id);
				that.$bvModal.show('file-records-modal');
				return HTTP.get(`srilanka/view-luminex-file-records`, {
					params: params
				}).then(response => {
					that.modalTotalRows = response.data.total_rows;
					that.modal_items =  response.data.row_data;
				}).catch((error) => {
					HandleErrors.HandleErrors(error);
				});
			} else {
				console.log('Not authorized to edit records.')
			}
		},
        refresh_files_table: function () {
            this.$refs.fileTable.refresh();
        },
		resetModal() {
			this.deleteConfirmed = ''
			this.deleteConfirmState = null
		},
		handleOk(bvModalEvt) {
			// Prevent modal from closing
			bvModalEvt.preventDefault()
			// Trigger submit handler
			this.handleSubmit()
		},
		handleSubmit() {
			// Exit when the form isn't valid
			if (!this.deleteConfirmed) {
				this.deleteConfirmState = false;
				return;
			}
			// Hide the modal manually
			this.deleteFileModalShow = false;
			this.deleteVhtFile();
		},
		hideModal(modal_id) {
			console.log("Hiding modal: ", modal_id);
			this.$bvModal.hide(modal_id);
		}
	},
};
</script>
<style>
.luminex-table-cell {
	white-space: nowrap;
    margin: auto !important;
    padding: 3px !important;
    font-size: small !important;
    max-width: 100px;
}
.luminex-thead-class th {
  font-size: small !important;
}
</style>